import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';

const milestones = [
  { year: '2024', title: '网站创立', description: 'ASMR导航正式上线，开始为ASMR爱好者整理优质的asmr网站资源。' },
  { year: '2025', title: '资源破百', description: '收录的asmr网站超过100个，涵盖七大分类，成为国内领先的asmr资源导航平台。' },
  { year: '2025', title: '内容升级', description: '推出ASMR科普文章板块，帮助新手了解ASMR并找到最适合自己的asmr助眠网站。' },
  { year: '2026', title: '社区成长', description: '月活跃用户突破50万，每天为数万用户提供优质的asmr网站推荐服务。' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: '品质优先',
    description: '每个推荐的asmr网站都经过团队亲自体验和评估，确保推荐质量。我们只推荐最好的asmr网站。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户为本',
    description: '以用户需求为核心，不断优化分类和推荐算法，帮助每位用户快速找到心仪的asmr在线听平台。',
  },
  {
    icon: 'ri-lightbulb-line',
    title: '持续更新',
    description: '团队每周更新资源列表，紧跟ASMR领域的最新趋势，确保asmr网站推荐的时效性。',
  },
  {
    icon: 'ri-group-line',
    title: '社区驱动',
    description: '倾听用户反馈，不断改进服务。用户的每一个建议都是我们前行的动力。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q2tq4nlsngrm4llqjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      const parsed = JSON.parse(responseText);
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于ASMR导航',
    description: 'ASMR导航是最全的ASMR资源导航平台，致力于为ASMR爱好者提供优质的asmr网站推荐和资源指南。',
    url: 'https://asmr-navigation.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ASMR导航',
    url: 'https://asmr-navigation.com',
    description: '最全的ASMR资源导航平台',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@asmr-navigation.com',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-navigation.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://asmr-navigation.com/about' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'ASMR导航是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'ASMR导航是最全的ASMR资源导航平台，致力于为ASMR爱好者提供优质的asmr网站推荐和资源指南，涵盖助眠、白噪音、耳语等多种类型。',
        },
      },
      {
        '@type': 'Question',
        name: '如何推荐新的asmr网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过本页面的联系表单提交新的asmr网站推荐，我们的团队会进行审核评估后收录到相应的分类中。',
        },
      },
      {
        '@type': 'Question',
        name: 'ASMR导航推荐的都是免费资源吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们推荐的asmr资源平台大多提供免费内容，部分平台可能有付费增值服务。每个推荐的asmr网站页面都会标注相关信息。',
        },
      },
    ],
  };

  return (
    <Layout>
      <SEO
        title="关于我们 — 品牌介绍与联系方式"
        description="ASMR导航是最全的ASMR资源导航平台，致力于为ASMR爱好者提供优质的asmr网站推荐。了解我们的品牌故事、发展历程和联系方式。"
        keywords="asmr网站,asmr网站推荐,关于我们,asmr资源平台,asmr导航,联系我们"
        canonicalUrl="/about"
        ogType="website"
        schema={{ ...aboutSchema, ...orgSchema, ...breadcrumbSchema, ...faqSchema }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {/* Brand Introduction */}
        <section className="mb-14">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            关于ASMR导航
          </h1>
          <div className="max-w-3xl">
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              ASMR导航创立于2024年，是国内领先的<strong>asmr网站</strong>资源导航平台。
              我们致力于为ASMR爱好者提供最全面、最优质的<strong>asmr网站推荐</strong>服务，
              帮助每一位用户快速找到最适合自己的<strong>asmr助眠网站</strong>和<strong>asmr在线听</strong>平台。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              无论是想要寻找高质量的<strong>asmr白噪音网站</strong>来提升专注力，
              还是希望找到温暖的耳语ASMR频道来缓解失眠，
              ASMR导航都为你准备了经过精心筛选的资源清单。
              我们相信，好的<strong>asmr资源平台</strong>能够为生活带来宁静与放松。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              截至目前，ASMR导航已经收录了超过100个优质<strong>asmr网站</strong>，
              覆盖助眠放松、白噪音、耳语ASMR、角色扮演、吃播咀嚼音、触发音和综合平台等七大分类，
              月活跃用户超过50万，是国内最大的<strong>asmr网站</strong>导航平台之一。
            </p>
          </div>
        </section>

        {/* Core Values */}
        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            我们的理念
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="p-5 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className={`${v.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Milestones */}
        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            发展历程
          </h2>
          <div className="relative">
            <div className="absolute left-0 top-2 bottom-2 w-px bg-background-300 hidden sm:block"></div>
            <div className="space-y-6">
              {milestones.map((m, i) => (
                <div key={m.year} className="flex gap-5">
                  <div className="hidden sm:flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></div>
                  </div>
                  <div className="flex-1 p-4 bg-background-50 rounded-lg border border-background-200/70">
                    <span className="text-xs font-semibold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">
                      {m.year}
                    </span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mt-2 mb-1">{m.title}</h3>
                    <p className="text-xs text-foreground-500">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                name: '失眠患者小林',
                text: 'ASMR导航推荐的asmr助眠网站真的帮了我大忙！之前每晚都要辗转反侧一两个小时，现在听着白噪音十几分钟就能入睡。',
                rating: 5,
              },
              {
                name: '大学生小张',
                text: '作为一个ASMR新手，这个导航网站对我太有用了！分类清晰，每个asmr网站都有详细介绍，很快就找到了我喜欢的耳语类频道。',
                rating: 5,
              },
              {
                name: '职场人阿杰',
                text: '工作压力大的时候就上ASMR导航找个asmr在线听平台放松一下，推荐的trigger音效特别解压，已经成为我每天必看的网站了。',
                rating: 5,
              },
              {
                name: 'ASMR创作者小美',
                text: '作为一个ASMR内容创作者，这个平台帮我了解了很多同行的优秀作品，也给我的创作带来了很多灵感。',
                rating: 4,
              },
              {
                name: '心理学研究生王同学',
                text: '从专业角度来看，ASMR导航的资源分类非常科学合理，白噪音和耳语类内容的质量都很高，推荐给所有需要放松的人。',
                rating: 5,
              },
              {
                name: '全职妈妈李姐',
                text: '带娃压力大的时候就打开ASMR导航推荐的asmr网站听一会儿，真的能让心情平静下来，强烈推荐给各位妈妈！',
                rating: 5,
              },
            ].map((review) => (
              <div key={review.name} className="p-5 bg-background-50 rounded-lg border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${i < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed mb-3">&ldquo;{review.text}&rdquo;</p>
                <p className="text-xs font-medium text-foreground-800">{review.name}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            联系我们
          </h2>
          <div className="max-w-2xl">
            <p className="text-sm text-foreground-500 mb-6">
              有任何关于<strong>asmr网站推荐</strong>的问题、建议或合作意向，欢迎通过以下表单联系我们。
              我们会在48小时内回复你的消息。
            </p>
            <form
              onSubmit={handleContactSubmit}
              data-readdy-form
              className="space-y-4"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="anti-bot-field"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  type="text"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请简要描述你的来意"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>
              <button
                type="submit"
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                发送留言
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-accent-600">留言已发送！我们会尽快回复你。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}